import React, { useEffect, useRef, useState } from "react";
import { marked } from "marked";

export default function ChatBotPanel({ getCurrentSQL, setSQLQuery }) {
  const [conversation, setConversation] = useState([]);
  const [userPrompt, setUserPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const handleSubmit = async () => {
    const prompt = userPrompt.trim();
    if (!prompt) return;

    const newConversation = [...conversation, { role: "user", content: prompt }];
    setConversation(newConversation);
    setUserPrompt("");
    setLoading(true);

    try {
      const response = await fetch("/api/testping", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationHistory: newConversation.map((msg) => msg.content),
          userPrompt: prompt,
          previousSQLQuery: getCurrentSQL(),
        }),
      });

      const data = await response.json();

      setConversation([
        ...newConversation,
        { role: "bot", content: data.ChatResponse },
      ]);
      console.log("✅ Chatbot SQL output:", data.SQLQuery);

      setSQLQuery({
        native: {
          query: data.SQLQuery,
        },
      });
    } catch (error) {
      console.error("Chatbot error:", error);
      setConversation([
        ...newConversation,
        { role: "bot", content: "⚠️ Error getting response from chatbot." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Handle Shift+Enter vs Enter
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Auto-scroll to bottom when conversation updates
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation, loading]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        border: "1px solid #ccc",
        borderRadius: "8px",
        padding: "1rem",
      }}
    >
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          marginBottom: "1rem",
          paddingRight: "0.5rem",
        }}
      >
        {conversation.map((msg, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: "0.75rem",
              textAlign: msg.role === "user" ? "right" : "left",
              whiteSpace: "pre-wrap",
            }}
          >
            <strong>{msg.role === "user" ? "You" : "InscendoIQ"}:</strong>
            <div
              dangerouslySetInnerHTML={{
                __html: msg.role === "bot"
                  ? marked.parse(msg.content)
                  : marked.parseInline(msg.content),
              }}
              style={{
                display: "inline-block",
                textAlign: "left",
                background: msg.role === "bot" ? "#f5f5f5" : "transparent",
                padding: msg.role === "bot" ? "0.5rem" : "0",
                borderRadius: "6px",
                maxWidth: "100%",
              }}
            />
          </div>
        ))}
        {loading && <div><em>InscendoIQ is thinking...</em></div>}
      </div>

      <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end" }}>
        <textarea
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          placeholder="Ask something about your data..."
          style={{
            flex: 1,
            padding: "0.5rem",
            resize: "none",
            minHeight: "3rem",
            fontFamily: "inherit",
          }}
        />
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{ padding: "0.5rem 1rem", height: "100%" }}
        >
          {loading ? "..." : "Send"}
        </button>
      </div>
    </div>
  );
}
